/**
 * BuzzTalk REST client — typed wrappers around /api/chat endpoints.
 *
 * All requests go through the shared `apiRequest` which:
 *   - Attaches Authorization + X-Workspace-Id headers automatically
 *   - Handles 401 with token refresh
 *   - Throws ApiRequestError with status/code/message on non-2xx
 *
 * Response envelope is `{ success: true, data: T }` — we unwrap to `T`.
 */

import { apiRequest } from '../api/core';

export type ChatGroupType = 'TEAM' | 'PROJECT' | 'TOPIC' | 'CLIENT' | 'SYSTEM';
export type ChatMemberRole = 'ADMIN' | 'MEMBER';
export type ChatNotifyMode = 'ALL' | 'MENTIONS' | 'MUTED';
export type ChatMessageKind = 'USER' | 'SYSTEM' | 'TASK_CREATED' | 'TASK_DONE';
export type ChatAttachmentKind = 'IMAGE' | 'FILE';
export type ChatTaskStatus = 'TODO' | 'DOING' | 'DONE';

/** POST /chat/groups/:id/guests — `emailed: false` means share the link by hand. */
export interface GuestInviteCreated {
  inviteId: string;
  expiresAt: string;
  acceptUrl: string;
  emailed: boolean;
}

/** GET /chat/invite/:token — what the accept page renders before login. */
export interface GuestInviteDetails {
  email: string;
  workspaceName: string;
  groupName: string;
  /** True when this address already has a BuzzBit account. */
  isExistingUser: boolean;
}

/**
 * Result of accepting a guest invite.
 *
 * `token` is present ONLY when this call created the account. An address that
 * already has an account must arrive already signed in as itself — the invite
 * token is handed to the inviter, so minting a session for an existing account
 * would be an account-takeover primitive — and it keeps the session it has.
 */
export interface GuestInviteAcceptResult {
  token?: string;
  user: { id: string; email: string; name: string | null };
  workspaceId: string;
  groupId: string;
}

export interface ChatGroup {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  type: ChatGroupType;
  avatarUrl: string | null;
  pinnedMessageId: string | null;
  lastMessageAt: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  unreadCount?: number;
}

export interface ChatAttachment {
  id: string;
  messageId: string;
  kind: ChatAttachmentKind;
  url: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface ChatReactionLite {
  emoji: string;
  userId: string;
}

export interface ChatMessage {
  id: string;
  groupId: string;
  authorId: string | null;
  body: string;
  mentions: string[];
  replyToId: string | null;
  kind: ChatMessageKind;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  attachments?: ChatAttachment[];
  reactions?: ChatReactionLite[];
  author?: { id: string; name: string; avatar: string | null } | null;
  replyTo?: { id: string; body: string; authorId: string | null; deletedAt: string | null } | null;
}

export interface ChatGroupMember {
  id: string;
  groupId: string;
  userId: string;
  role: ChatMemberRole;
  joinedAt: string;
  lastReadAt: string;
  notifyMode: ChatNotifyMode;
  mutedUntil: string | null;
  user?: { id: string; name: string; email: string; avatar: string | null } | null;
}

export interface ChatTask {
  id: string;
  groupId: string;
  workspaceId: string;
  title: string;
  description: string | null;
  status: ChatTaskStatus;
  assigneeId: string | null;
  createdById: string;
  dueAt: string | null;
  createdFromMessageId: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  assignee?: { id: string; name: string; avatar: string | null } | null;
  createdBy?: { id: string; name: string; avatar: string | null } | null;
}

export interface UploadedAttachment {
  kind: ChatAttachmentKind;
  url: string;
  mimeType: string;
  sizeBytes: number;
  path: string;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  meta?: { page?: number; limit?: number; total?: number };
}

async function get<T>(path: string): Promise<T> {
  const res = await apiRequest<SuccessEnvelope<T>>(path);
  return res.data;
}
async function post<T, B = unknown>(path: string, body?: B): Promise<T> {
  const res = await apiRequest<SuccessEnvelope<T>>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return res.data;
}
async function patch<T, B = unknown>(path: string, body: B): Promise<T> {
  const res = await apiRequest<SuccessEnvelope<T>>(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return res.data;
}
async function del<T>(path: string): Promise<T> {
  const res = await apiRequest<SuccessEnvelope<T>>(path, { method: 'DELETE' });
  return res.data;
}

// ==================== Groups ====================

export const chatApi = {
  listGroups(opts?: { limit?: number; archived?: boolean }): Promise<ChatGroup[]> {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set('limit', String(opts.limit));
    if (opts?.archived) qs.set('archived', 'true');
    const q = qs.toString();
    return get<ChatGroup[]>(`/chat/groups${q ? `?${q}` : ''}`);
  },

  createGroup(input: {
    name: string;
    description?: string;
    type?: Exclude<ChatGroupType, 'SYSTEM'>;
    memberIds?: string[];
  }): Promise<ChatGroup> {
    return post<ChatGroup, typeof input>('/chat/groups', input);
  },

  getGroup(groupId: string): Promise<ChatGroup> {
    return get<ChatGroup>(`/chat/groups/${groupId}`);
  },

  updateGroup(
    groupId: string,
    patchBody: { name?: string; description?: string | null; avatarUrl?: string | null },
  ): Promise<ChatGroup> {
    return patch<ChatGroup, typeof patchBody>(`/chat/groups/${groupId}`, patchBody);
  },

  archiveGroup(groupId: string): Promise<{ archived: boolean }> {
    return del<{ archived: boolean }>(`/chat/groups/${groupId}`);
  },

  unarchiveGroup(groupId: string): Promise<{ unarchived: boolean }> {
    return post<{ unarchived: boolean }>(`/chat/groups/${groupId}/unarchive`);
  },

  markRead(groupId: string): Promise<{ ok: boolean }> {
    return post<{ ok: boolean }>(`/chat/groups/${groupId}/read`);
  },

  pinMessage(groupId: string, messageId: string | null): Promise<ChatGroup> {
    return post<ChatGroup, { messageId: string | null }>(`/chat/groups/${groupId}/pin`, {
      messageId,
    });
  },

  // ==================== Members ====================

  listMembers(groupId: string): Promise<ChatGroupMember[]> {
    return get<ChatGroupMember[]>(`/chat/groups/${groupId}/members`);
  },

  addMember(groupId: string, userId: string, role?: ChatMemberRole): Promise<{ added: boolean }> {
    return post<{ added: boolean }, { userId: string; role?: ChatMemberRole }>(
      `/chat/groups/${groupId}/members`,
      { userId, role },
    );
  },

  removeMember(groupId: string, userId: string): Promise<{ removed: boolean }> {
    return del<{ removed: boolean }>(`/chat/groups/${groupId}/members/${userId}`);
  },

  setNotifyMode(groupId: string, mode: ChatNotifyMode): Promise<{ mode: ChatNotifyMode }> {
    return patch<{ mode: ChatNotifyMode }, { mode: ChatNotifyMode }>(
      `/chat/groups/${groupId}/notify`,
      { mode },
    );
  },

  // ==================== Messages ====================

  listMessages(
    groupId: string,
    opts?: { limit?: number; before?: string; since?: string; q?: string },
  ): Promise<ChatMessage[]> {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set('limit', String(opts.limit));
    if (opts?.before) qs.set('before', opts.before);
    if (opts?.since) qs.set('since', opts.since);
    if (opts?.q) qs.set('q', opts.q);
    const q = qs.toString();
    return get<ChatMessage[]>(`/chat/groups/${groupId}/messages${q ? `?${q}` : ''}`);
  },

  sendMessage(
    groupId: string,
    body: { body: string; replyToId?: string; attachments?: UploadedAttachment[]; metadata?: Record<string, unknown> },
  ): Promise<ChatMessage> {
    return post<ChatMessage, typeof body>(`/chat/groups/${groupId}/messages`, body);
  },

  editMessage(messageId: string, body: string): Promise<ChatMessage> {
    return patch<ChatMessage, { body: string }>(`/chat/messages/${messageId}`, { body });
  },

  deleteMessage(messageId: string): Promise<{ deleted: boolean }> {
    return del<{ deleted: boolean }>(`/chat/messages/${messageId}`);
  },

  toggleReaction(messageId: string, emoji: string): Promise<{
    action: 'add' | 'remove';
    messageId: string;
    userId: string;
    emoji: string;
  }> {
    return post<
      { action: 'add' | 'remove'; messageId: string; userId: string; emoji: string },
      { emoji: string }
    >(`/chat/messages/${messageId}/reactions`, { emoji });
  },

  // ==================== Tasks ====================

  listTasks(groupId: string): Promise<ChatTask[]> {
    return get<ChatTask[]>(`/chat/groups/${groupId}/tasks`);
  },

  createTask(
    groupId: string,
    body: { title: string; description?: string; assigneeId?: string; dueAt?: string },
  ): Promise<ChatTask> {
    return post<ChatTask, typeof body>(`/chat/groups/${groupId}/tasks`, body);
  },

  updateTask(
    taskId: string,
    body: {
      title?: string;
      description?: string | null;
      status?: ChatTaskStatus;
      assigneeId?: string | null;
      dueAt?: string | null;
    },
  ): Promise<ChatTask> {
    return patch<ChatTask, typeof body>(`/chat/tasks/${taskId}`, body);
  },

  deleteTask(taskId: string): Promise<{ deleted: boolean }> {
    return del<{ deleted: boolean }>(`/chat/tasks/${taskId}`);
  },

  promoteToTask(
    messageId: string,
    body?: { title?: string; assigneeId?: string; dueAt?: string },
  ): Promise<ChatTask> {
    return post<ChatTask, typeof body>(`/chat/messages/${messageId}/promote-to-task`, body);
  },

  // ==================== Uploads ====================

  async upload(file: File): Promise<UploadedAttachment> {
    const { getAuthToken, getWorkspaceId } = await import('../api/core');
    const { API_BASE_URL } = await import('../../config/api');
    const token = getAuthToken();
    const workspaceId = getWorkspaceId();
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE_URL}/chat/uploads`, {
      method: 'POST',
      body: fd,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Upload failed (${res.status}): ${text}`);
    }
    const json = (await res.json()) as SuccessEnvelope<UploadedAttachment>;
    return json.data;
  },

  // ==================== Guests ====================

  inviteGuest(groupId: string, email: string): Promise<GuestInviteCreated> {
    return post<GuestInviteCreated, { email: string }>(`/chat/groups/${groupId}/guests`, { email });
  },

  /** Accept as the currently signed-in user (they were already logged in). */
  acceptGuestInvite(token: string): Promise<{ workspaceId: string; groupId: string }> {
    return post<{ workspaceId: string; groupId: string }>(`/chat/guests/accept/${token}`);
  },

  // Public (unauthenticated) magic-link endpoints — the emailed
  // /chat/accept/:token page. A guest is external, so these must work with no
  // session at all; `acceptGuestInvitePublic` returns a full login.
  getGuestInvite(token: string): Promise<GuestInviteDetails> {
    return get<GuestInviteDetails>(`/chat/invite/${token}`);
  },

  acceptGuestInvitePublic(
    token: string,
    body: { password?: string; name?: string },
  ): Promise<GuestInviteAcceptResult> {
    return post<GuestInviteAcceptResult, { password?: string; name?: string }>(
      `/chat/invite/${token}/accept`,
      body,
    );
  },

  revokeGuest(userId: string): Promise<{ revoked: boolean }> {
    return del<{ revoked: boolean }>(`/chat/guests/${userId}`);
  },

  // ==================== Realtime JWT bridge ====================
  // Mints a 1h Supabase-compatible JWT signed with SUPABASE_JWT_SECRET so the
  // browser can authenticate Realtime subscriptions and pass RLS policies that
  // reference `auth.uid()`. Token never persisted; caller passes to
  // `setRealtimeSession()` directly.
  getRealtimeToken(): Promise<{ token: string; expiresIn: number }> {
    return post<{ token: string; expiresIn: number }>('/chat/realtime-token');
  },
};

export default chatApi;
